import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { GeneralComponentClass, Service } from 'webapp-types';

import didInsert from 'webapp/modifiers/did-insert';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CustomQuerySetupWizardSheetNavigationComponent from 'webapp/components/custom-query/setup/wizard/sheet/navigation';

import type CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';
import type CustomMetricSetupFormSheet from 'webapp/models-custom/custom-metric-setup-form/sheet';
import type CustomQuery from 'webapp/models-custom/custom-query';

export interface Args {
	model: CustomQuery;
	form: CustomMetricSetupFormSheet | CustomMetricSetupFormApiMetricBuilder;
	origin: string;
	toggleWizardFlow: () => void;
	afterQuerySave: () => void;
}

interface NavigationStep {
	id: string;
	text: string;
	component: GeneralComponentClass;
}

export default abstract class CustomQuerySetupWizardBaseComponent<
	T extends Args,
> extends Component<T> {
	@service('analytics') declare analyticsService: Service.Analytics;

	@tracked controlsNode: HTMLElement | null = null;

	@tracked stepsDone: string[] = [];

	@tracked abstract stepId: string;
	abstract wizardType: string;
	abstract get allNavigationSteps(): NavigationStep[];
	abstract goToPrevOrNextStep(fromStepId: string, isPrev: boolean): void;
	abstract selectInitialStep(): void;
	abstract trackWizardStep(): void;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.selectInitialStep();
	}

	get showNavigation(): boolean {
		return true; // can be overriden
	}

	get activeStepComponent() {
		return this.allNavigationSteps.find((item) => item.id === this.stepId)?.component;
	}

	get navigationSteps(): SomeDataObject[] {
		let activeStepIndex = Infinity;
		return this.allNavigationSteps.map(({ id, text }, idx) => {
			const isActive = id === this.stepId;
			if (isActive) {
				activeStepIndex = idx;
			}
			return {
				id,
				text,
				isActive,
				isDone: idx < activeStepIndex,
			};
		});
	}

	get activeStepAlreadyVisited(): boolean {
		return this.stepsDone.includes(this.stepId);
	}

	goToStep(stepId: string): void {
		this.stepId = stepId;
		this.trackWizardStep();
	}

	@action
	setupControlsNode(node: HTMLElement): void {
		this.controlsNode = node;
	}

	@action
	openFromNavigation(stepId: string): void {
		const step = this.navigationSteps.find((item) => item.id === stepId);
		if (!(step?.isActive || step?.isDone)) {
			return;
		}
		this.goToStep(stepId);
	}

	@action
	goPrevious(): void {
		this.goToPrevOrNextStep(this.stepId, true);
	}

	@action
	goNext(): void {
		if (!this.stepsDone.includes(this.stepId)) {
			this.stepsDone = [...this.stepsDone, this.stepId];
		}
		this.goToPrevOrNextStep(this.stepId, false);
	}
	<template>
		<div
			class="custom-metric-wizard custom-metric-wizard--{{this.wizardType}}
				step--{{this.stepId}}"
		>
			<div class="cmw-content">
				{{#if this.showNavigation}}
					<CustomQuerySetupWizardSheetNavigationComponent
						@steps={{this.navigationSteps}}
						@open={{this.openFromNavigation}}
					/>
				{{/if}}

				{{#let (component this.activeStepComponent) as |ComponentName|}}
					<ComponentName
						@model={{@model}}
						@form={{@form}}
						@stepId={{this.stepId}}
						@isAlreadyVisited={{this.activeStepAlreadyVisited}}
						@origin={{@origin}}
						@controlsNode={{this.controlsNode}}
						@goNext={{this.goNext}}
						@goPrevious={{this.goPrevious}}
					>
						<div class="toggle-manual">
							<a class="button theme-white" {{on "click" @toggleWizardFlow}}>
								<CommonUiIconComponent @name="Wrench" @size="sm" />Switch to manual
								setup
							</a>
						</div>
					</ComponentName>
				{{/let}}
			</div>

			{{! used as in-element target }}
			<div class="cmw-controls" {{didInsert this.setupControlsNode}}></div>

		</div>
	</template>
}
